// Modal de "Permissões do banco" — professor escolhe quais alunos do escopo
// dele (turmas onde ele leciona) têm acesso direto a esse banco.
//
// Endpoint: /api/teacher/bank-grants
// - GET ?bankId=X  → entries dos alunos atualmente granteados
// - PUT { bankId, userId, canTakeExam } → upsert
// - DELETE ?bankId=X&userId=Y → revoga
//
// O universo de alunos vem da matriz (GET sem filtro) — alunos das turmas
// das disciplinas onde o prof leciona.

import { useEffect, useState } from "preact/hooks";
import {
  Check,
  CheckCircle2,
  Database,
  Loader2,
  Search,
  Shield,
  Trash2,
  Users,
  X
} from "lucide-react";
import { api } from "../api";
import { openAlert, openConfirm } from "../ConfirmPromptModal";
import { friendlyError } from "@app/ui/lib/friendly-error";

interface Bank {
  id: string;
  name: string;
}
interface Student {
  id: string;
  email: string;
  name: string;
  apelido: string | null;
}
interface Entry {
  userId: string;
  email: string;
  name: string;
  canTakeExam: boolean;
  grantedAt: string;
}

export function BankGrantsModal({
  bank,
  onClose
}: {
  bank: Bank;
  onClose: () => void;
}) {
  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState<Student[]>([]);
  const [entries, setEntries] = useState<Entry[]>([]);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [picking, setPicking] = useState(false);

  async function reload() {
    setLoading(true);
    try {
      // Universo de alunos do prof (mesma chamada que monta a matriz)
      const matrix = await api.bankGrantsMatrix();
      setStudents(matrix.students);
      // Filtra grants só deste banco
      const ents = await api.bankGrantsByBank(bank.id);
      setEntries(ents.entries);
    } catch (err) {
      void openAlert({
        title: "Erro",
        message:
          err instanceof Error ? err.message : "Falha ao carregar permissões"
      });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void reload();
  }, [bank.id]);

  async function addUser(userId: string) {
    setBusy(userId);
    try {
      await api.putBankGrant({ bankId: bank.id, userId, canTakeExam: true });
      await reload();
      setPicking(false);
    } catch (err) {
      void openAlert({
        title: "Erro",
        message: friendlyError(err, { fallback: "Falha ao adicionar" })
      });
    } finally {
      setBusy(null);
    }
  }

  async function toggleExam(entry: Entry) {
    setBusy(entry.userId);
    try {
      await api.putBankGrant({
        bankId: bank.id,
        userId: entry.userId,
        canTakeExam: !entry.canTakeExam
      });
      await reload();
    } catch (err) {
      void openAlert({
        title: "Erro",
        message: friendlyError(err, { fallback: "Falha ao atualizar" })
      });
    } finally {
      setBusy(null);
    }
  }

  async function revoke(entry: Entry) {
    const ok = await openConfirm({
      title: "Revogar acesso?",
      message: `Tirar acesso de ${entry.email} a "${bank.name}"?`,
      confirmLabel: "Revogar",
      danger: true
    });
    if (!ok) return;
    setBusy(entry.userId);
    try {
      await api.deleteBankGrant(bank.id, entry.userId);
      await reload();
    } catch (err) {
      void openAlert({
        title: "Erro",
        message: friendlyError(err, { fallback: "Falha ao revogar" })
      });
    } finally {
      setBusy(null);
    }
  }

  const grantedIds = new Set(entries.map((e) => e.userId));
  const eligible = students.filter((s) => !grantedIds.has(s.id));
  const q = search.toLowerCase().trim();
  const eligibleFiltered = q
    ? eligible.filter((s) =>
        `${s.email} ${s.name} ${s.apelido ?? ""}`.toLowerCase().includes(q)
      )
    : eligible;

  // Modal admin de gestão de permissões — não fecha por click no backdrop
  // pra evitar perda do estado intermediário. [[feedback-modal-no-click-outside-close]]

  return (
    <div
      class="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
    >
      <div class="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-md border border-border bg-card shadow-2xl">
        <header class="flex items-center justify-between gap-3 border-b border-border px-5 py-3">
          <h2 class="flex min-w-0 items-center gap-2 text-base font-bold">
            <Shield className="size-4 shrink-0 text-primary" />
            <span class="truncate">Permissões — {bank.name}</span>
          </h2>
          <button
            type="button"
            onClick={onClose}
            title="Fechar"
            aria-label="Fechar"
            class="grid size-8 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            <X className="size-4" />
          </button>
        </header>

        <div class="flex-1 space-y-4 overflow-y-auto p-5">
          <p class="rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-[11px] text-foreground/80">
            Aqui você libera o banco pra alunos das suas turmas. Você só
            consegue grantar quem está matriculado em alguma turma de uma
            disciplina onde você leciona.
          </p>

          {loading ? (
            <p class="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" /> Carregando…
            </p>
          ) : (
            <>
              <section>
                <h3 class="mb-2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                  <CheckCircle2 className="size-3.5" />
                  Alunos com acesso ({entries.length})
                </h3>
                {entries.length === 0 ? (
                  <p class="rounded-md border border-dashed border-border bg-background/40 px-3 py-4 text-center text-xs text-muted-foreground">
                    Ninguém tem acesso direto a este banco ainda.
                  </p>
                ) : (
                  <ul class="space-y-1.5">
                    {entries.map((e) => (
                      <li
                        key={e.userId}
                        class="flex items-center gap-2 rounded-md border border-border bg-background/60 px-3 py-2 text-sm"
                      >
                        <div class="min-w-0 flex-1">
                          <div class="truncate font-semibold">{e.name}</div>
                          <div class="truncate text-[11px] text-muted-foreground">
                            {e.email}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => toggleExam(e)}
                          disabled={busy === e.userId}
                          title={
                            e.canTakeExam
                              ? "Pode fazer prova simulada"
                              : "Só estuda — não pode fazer prova"
                          }
                          class={`inline-flex shrink-0 items-center gap-1 rounded-md border px-2 py-1 text-[11px] font-semibold ${
                            e.canTakeExam
                              ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                              : "border-border bg-background text-muted-foreground hover:bg-accent"
                          }`}
                        >
                          <Check className="size-3" />
                          {e.canTakeExam ? "Prova OK" : "Só estudo"}
                        </button>
                        <button
                          type="button"
                          onClick={() => revoke(e)}
                          disabled={busy === e.userId}
                          title="Revogar acesso"
                          aria-label="Revogar acesso"
                          class="grid size-7 shrink-0 place-items-center rounded-md text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                        >
                          {busy === e.userId ? (
                            <Loader2 className="size-3.5 animate-spin" />
                          ) : (
                            <Trash2 className="size-3.5" />
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <section>
                <div class="mb-2 flex items-center justify-between gap-2">
                  <h3 class="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                    <Users className="size-3.5" />
                    Adicionar aluno
                  </h3>
                  <button
                    type="button"
                    onClick={() => setPicking((p) => !p)}
                    class="rounded-md border border-border bg-background px-2 py-1 text-[11px] font-semibold hover:bg-accent"
                  >
                    {picking ? "Fechar lista" : "Ver alunos elegíveis"}
                  </button>
                </div>

                {picking && (
                  <div class="space-y-2 rounded-md border border-border bg-background/40 p-3">
                    <div class="relative">
                      <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
                      <input
                        type="text"
                        value={search}
                        onInput={(e) =>
                          setSearch((e.target as HTMLInputElement).value)
                        }
                        placeholder="Buscar nome ou email…"
                        class="w-full rounded-md border border-border bg-background py-1.5 pl-8 pr-3 text-xs outline-none focus:border-primary"
                      />
                    </div>
                    {eligibleFiltered.length === 0 ? (
                      <p class="px-2 py-3 text-center text-xs text-muted-foreground">
                        {eligible.length === 0
                          ? "Todos os alunos do seu escopo já têm acesso."
                          : "Nenhum aluno bate com a busca."}
                      </p>
                    ) : (
                      <ul class="max-h-64 space-y-1 overflow-y-auto scroll-thin">
                        {eligibleFiltered.map((s) => (
                          <li key={s.id}>
                            <button
                              type="button"
                              onClick={() => addUser(s.id)}
                              disabled={busy === s.id}
                              class="flex w-full items-center gap-2 rounded-md border border-border bg-card px-2.5 py-1.5 text-left text-xs hover:bg-accent disabled:opacity-50"
                            >
                              <Database className="size-3.5 shrink-0 text-muted-foreground" />
                              <div class="min-w-0 flex-1">
                                <div class="truncate font-semibold">
                                  {s.name}
                                </div>
                                <div class="truncate text-[10px] text-muted-foreground">
                                  {s.email}
                                </div>
                              </div>
                              {busy === s.id && (
                                <Loader2 className="size-3.5 animate-spin text-muted-foreground" />
                              )}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
